/**
 * cameraService
 * -----------------------------------------------------------------------------
 * Servicio de cámara FOTO. Lógica pura: NO renderiza componentes React y NO
 * sube nada al backend (la subida es otro ticket). Su única responsabilidad es
 * obtener una foto usable de forma uniforme entre nativo y web.
 *
 * - En app NATIVA (android/ios) usa el bridge `tiger*` (ver TIGER_BRIDGE.md 4.1):
 *     disparo  -> tigerStartCamera()
 *     respuesta -> window.tigerGetPicture(base64)
 * - En WEB (navegador/desktop sin bridge) usa un <input type="file" capture>.
 *
 * Ambas plataformas resuelven con la MISMA forma: { dataUrl, file }.
 * -----------------------------------------------------------------------------
 */

import {
  getPlatform,
  isNativeApp,
  androidCall,
  iosPostMessage,
  awaitNativeCallback,
} from './nativeBridge';
import { dataUrlToFile } from '../utils/dataUrlToFile';

/**
 * Guard SSR: hay objeto window disponible.
 * @returns {boolean}
 */
function hasWindow() {
  return typeof window !== 'undefined';
}

/**
 * Guard SSR: hay objeto document disponible (necesario para el fallback web).
 * @returns {boolean}
 */
function hasDocument() {
  return typeof document !== 'undefined';
}

/**
 * Construye el Error normalizado de "no se capturó foto" (cancelación silenciosa).
 * El consumidor debe distinguir este caso (error.code === 'NO_PHOTO') del resto.
 * @returns {Error}
 */
function buildNoPhotoError() {
  const error = new Error('No se capturó ninguna foto');
  error.code = 'NO_PHOTO';
  return error;
}

/**
 * Indica si un error del bridge o del flujo de captura equivale a cancelación
 * silenciosa (sin foto).
 * @param {Error} error
 * @returns {boolean}
 */
function isNoPhotoError(error) {
  if (!error) {
    return false;
  }
  if (error.code === 'NO_PHOTO') {
    return true;
  }
  // TIMEOUT: usuario cerró la cámara sin sacar foto.
  // REPLACED: otra captura reemplazó la pendiente anterior.
  return error.code === 'TIMEOUT' || error.code === 'REPLACED';
}

/**
 * Deriva la extensión de archivo a partir del mime.
 * jpeg -> jpg, png -> png, fallback -> jpg.
 * @param {string} mime
 * @returns {string}
 */
function mimeToExt(mime) {
  if (mime && /png/i.test(mime)) {
    return 'png';
  }
  // jpeg/jpg y cualquier otro caso caen en jpg por defecto.
  return 'jpg';
}

/**
 * Extrae el mime del prefijo de un dataURL ('data:image/png;base64,...').
 * @param {string} dataUrl
 * @returns {string} mime o '' si no se pudo parsear.
 */
function parseMimeFromDataUrl(dataUrl) {
  const match = /^data:(.*?);/.exec(dataUrl || '');
  return match ? match[1] : '';
}

/**
 * Nombre de archivo por defecto: evidencia_<timestamp>.<ext>.
 * @param {string} mime
 * @returns {string}
 */
function buildDefaultFileName(mime) {
  return `evidencia_${Date.now()}.${mimeToExt(mime)}`;
}

/**
 * Normaliza el base64 que entrega el nativo a un dataURL completo.
 * Si ya viene como 'data:...', se usa tal cual; si viene "pelado", se le
 * antepone el prefijo jpeg.
 *
 * NOTA: el formato exacto que entrega el nativo está POR CONFIRMAR en el primer
 * test en dispositivo (ver TIGER_BRIDGE.md 4.1). Asumimos jpeg por defecto.
 *
 * @param {string} base64
 * @returns {string} dataURL
 */
function normalizeToDataUrl(base64) {
  const value = String(base64);
  if (value.startsWith('data:')) {
    return value;
  }
  return `data:image/jpeg;base64,${value}`;
}

/**
 * Flujo NATIVO: dispara la cámara del wrapper y espera el callback.
 * Respeta el ORDEN OBLIGATORIO del bridge: registrar el await ANTES de disparar.
 *
 * @param {Object} params
 * @param {string} [params.fileName]
 * @param {number} params.timeoutMs
 * @returns {Promise<{ dataUrl: string, file: File }>}
 */
async function capturePhotoNative({ fileName, timeoutMs }) {
  // 1º registrar la espera, 2º disparar (el nativo puede responder muy rápido).
  const pending = awaitNativeCallback('tigerGetPicture', { timeoutMs });

  if (getPlatform() === 'android') {
    androidCall('tigerStartCamera');
  } else {
    iosPostMessage('tigerStartCamera', {});
  }

  const base64 = await pending;

  // base64 vacío/falsy => se trata como "sin foto".
  if (!base64) {
    throw buildNoPhotoError();
  }

  const dataUrl = normalizeToDataUrl(base64);
  const mime = parseMimeFromDataUrl(dataUrl);
  const finalName = fileName || buildDefaultFileName(mime);

  const file = dataUrlToFile(dataUrl, finalName);
  if (!file) {
    // base64 no vacío pero no convertible => error real (no es cancelación).
    throw new Error('No se pudo convertir la foto capturada a archivo');
  }

  return { dataUrl, file };
}

/**
 * Flujo WEB (fallback sin bridge): input file programático con captura.
 * Limpia SIEMPRE el input del DOM al terminar (resuelva, rechace o cancele).
 *
 * @returns {Promise<{ dataUrl: string, file: File }>}
 */
function capturePhotoWeb() {
  if (!hasWindow() || !hasDocument()) {
    return Promise.reject(new Error('Cámara web no disponible (sin window/document)'));
  }

  let input = null;

  const cleanup = () => {
    if (input && input.parentNode) {
      input.parentNode.removeChild(input);
    }
    input = null;
  };

  return new Promise((resolve, reject) => {
    input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.style.display = 'none';

    const onChange = () => {
      const file = input && input.files && input.files[0];

      // 'change' sin archivo => el usuario no eligió foto.
      if (!file) {
        reject(buildNoPhotoError());
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        // En web el File es directamente el del input (no se reconstruye).
        resolve({ dataUrl: reader.result, file });
      };
      reader.onerror = () => {
        reject(reader.error || new Error('Error leyendo la imagen seleccionada'));
      };
      reader.readAsDataURL(file);
    };

    // 'cancel' no está soportado en todos los browsers; cuando existe, cubre el
    // caso de cerrar el selector sin elegir archivo.
    const onCancel = () => {
      reject(buildNoPhotoError());
    };

    input.addEventListener('change', onChange);
    input.addEventListener('cancel', onCancel);

    document.body.appendChild(input);
    input.click();
  }).finally(cleanup);
}

/**
 * Toma una foto y la devuelve como { dataUrl, file }, eligiendo automáticamente
 * el flujo nativo (bridge tiger*) o el fallback web según la plataforma.
 *
 * Manejo de cancelación: cuando el usuario cierra la cámara sin sacar foto, el
 * nativo NO avisa y se manifiesta como timeout del bridge; junto con base64
 * vacío, reemplazo de pendiente y el 'cancel' del input web, se normaliza a:
 *   new Error('No se capturó ninguna foto') con error.code === 'NO_PHOTO'.
 *
 * El consumidor DEBE distinguir:
 *   - error.code === 'NO_PHOTO' -> cancelación silenciosa, NO mostrar error.
 *   - cualquier otro error       -> error real, mostrarlo.
 *
 * @param {Object} [options]
 * @param {string} [options.fileName]        Nombre del archivo resultante. Si no
 *                                           se pasa, se usa evidencia_<ts>.<ext>.
 *                                           (En web no se reconstruye el File: el
 *                                           nombre lo define el archivo elegido.)
 * @param {number} [options.timeoutMs=120000] Timeout de espera del callback nativo.
 * @returns {Promise<{ dataUrl: string, file: File }>}
 */
export async function takePhoto({ fileName, timeoutMs = 120000 } = {}) {
  try {
    if (isNativeApp()) {
      return await capturePhotoNative({ fileName, timeoutMs });
    }
    return await capturePhotoWeb();
  } catch (error) {
    if (isNoPhotoError(error)) {
      throw buildNoPhotoError();
    }
    throw error;
  }
}
